import formatListString from "@/helpers/formatListString"

/**
 * Formats an array of weekday numbers into a comma-separated string of Dutch
 * day abbreviations.
 *
 * @param {number[]} weekdays - Array of weekday numbers (0=Monday)
 * @param {boolean} includeAnd - Whether to include "and" before the last item in the list
 * @returns {string} Comma-separated day abbreviations (e.g., "ma, di, wo") or
 * empty string
 */
export function formatWeekdaysString(weekdays, includeAnd = false) {
  if (!weekdays || weekdays.length === 0) {
    return ""
  }

  const dayNames = ["ma", "di", "wo", "do", "vr", "za", "zo"]
  const dayLabels = [...new Set(weekdays)]
    .sort((a, b) => a - b)
    .map((day) => dayNames[day])
  return formatListString(dayLabels, includeAnd)
}

/**
 * Formats an array of hours into time range strings. Groups consecutive hours
 * into ranges and formats them as time slots.
 *
 * Examples:
 * - [9, 10, 11, 12] -> "9:00 - 13:00"
 * - [18, 22, 23] -> "18:00 - 19:00, 22:00 - 0:00"
 *
 * @param {number[]} hours - Array of hour numbers (0-23)
 * @param {boolean} includeAnd - Whether to include "en" before the last item in the list
 * @returns {string} Formatted time ranges (e.g., "9:00 - 13:00, 18:00 - 19:00")
 * or empty string
 */
export function formatHoursString(hours, includeAnd = false) {
  if (!hours || hours.length === 0) {
    return ""
  }

  // Group consecutive hours into ranges
  const sortedHours = [...hours].sort((a, b) => a - b)
  const ranges = []
  let rangeStart = sortedHours[0]
  let rangeEnd = sortedHours[0]

  for (let i = 1; i < sortedHours.length; i++) {
    if (sortedHours[i] === rangeEnd + 1) {
      rangeEnd = sortedHours[i]
    } else {
      ranges.push([rangeStart, rangeEnd])
      rangeStart = sortedHours[i]
      rangeEnd = sortedHours[i]
    }
  }
  ranges.push([rangeStart, rangeEnd])

  // Format ranges as time strings
  const timeStrings = ranges.map(([start, end]) => {
    const startTime = `${start}:00`
    const endTime = `${(end + 1) % 24}:00`
    return `${startTime} - ${endTime}`
  })

  return formatListString(timeStrings, includeAnd)
}
